import TextSection from '../components/section/text_section';
import ImageSection from '../components/section/image_section';
import PullQuoteSection from '../components/section/pull_quote_section';
import HTMLSection from '../components/section/html_section';
import InternalVideoSection from '../components/section/internal_video_section';
import YoutubeSection from '../components/section/youtube_section';
import FacebookVideoSection from '../components/section/facebook_video_section';
import GetEmbedGallerySection from '../components/section/get_embed_gallery_section';
import GetEmbedRevealSection from '../components/section/get_embed_reveal_section';
import MultiImageSection from '../components/section/multi_image_section';
import DividerSection from '../components/section/divider_section';
import MultiProductSection from '../components/section/multi_product_section';
import { isYoutube, isFacebookVideo } from './widgetInspector';
import classNames from 'classnames';
import {get, every, compact, includes, isObject, partialRight} from 'lodash';

/*
 *******************************************************************
 * Functions with test coverage in spec/unit/utils/sections_spec.js *
 *******************************************************************
 */

export const isAssetSection = (section) => isObject(section.content);

const getAssetSectionContainerClassNames = ({
  content: { alignment, width_percentage },
  polyptychAttributes
}) => compact([
  'section-asset-container',
  alignment
    ? `${alignment}-align`
    : null,
  width_percentage
    ? `${width_percentage.toLowerCase()}-width`
    : null,
  get(polyptychAttributes, 'isMiddleOfTriptych')
    ? 'middle-of-triptych'
    : null
]);

export const isMultiProductSection = (section) =>
  section.type === 'blog_asset_module' && section.content.template === 'multi_product';

const getProductSectionContainerClassNames = ({ content: { products } }) => {
  const numProducts = products.length;
  const alignment = numProducts < 3 ? 'left-align' : '';
  let width;

  if (numProducts === 1) {
    width = 'third-width';
  } else if (numProducts === 2) {
    width = 'two-thirds-width';
  } else {
    width = 'full-width';
  }

  return [`section-asset-container ${alignment} ${width}`];
};

export const getSectionContainerClassNames = (section) => {
  if (isAssetSection(section)) {
    if (isMultiProductSection(section)) {
      return classNames(['section-container', ...getProductSectionContainerClassNames(section)]);
    }

    return classNames(['section-container', ...getAssetSectionContainerClassNames(section)]);
  }

  return classNames(['section-container', 'section-text-container']);
};

/*
 * Given an array of sections from Bridge, this method returns the array of
 * text sections that have text content over 160 chars. Each object in the array
 * contains its original index within the sections from Bridge and text content.
 *
 * Implementation Details:
 *
 * The article content we get from Bridge lives in the sections object,
 * and contains multiple types of section data (not just text).
 *
 * Each text section often includes embeded HTML from the Dash story editor.
 * This adds length to the section in Bridge, but does not add length when
 * the article is rendered
 *
 * Thus, allParagraphs() returns the true number of text paragraphs by:
 *   1. Filtering out non-text sections from the array of sections
 *   2. Parsing out any anchor tags and other embeded HTML using some regex
 *   3. Filtering out subtitles based on character counts being less than 160
 *   4. Counting the number of sections leftover with content.length > 160
 */
export const allParagraphs = (sections) => {
  const paragraphsWithoutTags = compact(sections.map((section, index) => {
    if (section.type === 'text') {
      const matchHyperlinks = /<a [^>]+>/g;
      const matchAllHtmlTags = /<[^>]*>/g;
      const filteredHTML = section.content.replace(matchHyperlinks, '').replace(matchAllHtmlTags, '');

      return {
        index: index,
        content: filteredHTML
      };
    }
  }));

  return paragraphsWithoutTags.filter(paragraph =>
    paragraph.content.length > 160
  );
};

/*
 ***************************************************************************
 * Functions with test coverage in the components/reducers in which they're *
 * used                                                                     *
 ***************************************************************************
 */

export const isImageSection = section =>
  section && /^image/.test(section.type);

export const isHTMLSection = section =>
  section && /^widget/.test(section.type);

export const isHTMLVideoSection = section =>
  isHTMLSection(section) && (
    isYoutube(section.content.resource) || isFacebookVideo(section.content.resource)
  );

export const isVideoSection = section =>
  section && (/^video/.test(section.type) || isHTMLVideoSection(section));

const isLeftOrRightAligned = section => includes([
  'left',
  'right'
], get(section, 'content.alignment'));

const isFullWidthOrFullBleed = section => includes([
  'FULL',
  'PAGE'
], get(section, 'content.width_percentage'));

export const isFloatingSection = section =>
  isLeftOrRightAligned(section) && !isFullWidthOrFullBleed(section);

export const wasCreatedInOldStoryEditor = ({ content }) =>
  !content.width_percentage;

export const isPolyptychFromOldStoryEditor =
  partialRight(
    every, section =>
      isLeftOrRightAligned(section)
      && isImageSection(section)
      && wasCreatedInOldStoryEditor(section)
  );

export const isPolyptychFromNewStoryEditor = sections =>
  (
    sections.length === 2
    && every(sections, isImageSection)
    && sections[0].content.alignment === 'left'
    && sections[0].content.width_percentage === 'HALF'
    && sections[1].content.alignment === 'right'
    && sections[1].content.width_percentage === 'HALF'
  ) || (
    sections.length === 3
    && every(sections, isImageSection)
    && sections[0].content.alignment === 'left'
    && sections[0].content.width_percentage === 'THIRD'
    && sections[1].content.alignment === 'center'
    && sections[1].content.width_percentage === 'THIRD'
    && sections[2].content.alignment === 'right'
    && sections[2].content.width_percentage === 'THIRD'
  );

/*
 * Shim body section's alignment if alignment is present and an empty string.
 */
export const shouldShimAlignment = section =>
  get(section, 'content.alignment') === '';

/*
 * Uses the section's type in combination with the section's content
 * in order to ascertain the correct section constructor
 */
export const getSectionConstructor = (section) => {
  switch (section.type) {
    case 'text':
      return TextSection;
    case 'widget:pull_quote':
      return PullQuoteSection;
    case 'video:internal':
      if (section.content.youtube_video_id) {
        return YoutubeSection;
      } else if (section.content.facebook_video_id) {
        return FacebookVideoSection;
      }
      return InternalVideoSection;
    case 'video:external': // OSE
    case 'widget:anchor':
    case 'widget:html': // NSE
      if (isYoutube(section.content.resource)) {
        return YoutubeSection;
      }
      if (isFacebookVideo(section.content.resource)) {
        return FacebookVideoSection;
      }
      return HTMLSection;
    case 'image:png':
    case 'image:gif':
    case 'image:jpg':
      return ImageSection;
    case 'blog_asset_module':
      if (section.content.template === 'gallery') {
        return GetEmbedGallerySection;
      }
      if (section.content.template === 'reveal') {
        return GetEmbedRevealSection;
      }
      if (section.content.template === 'multi_image') {
        return MultiImageSection;
      }
      if (section.content.template === 'multi_product') {
        return MultiProductSection;
      }
      break;
    case 'blog_entry_divider':
      return DividerSection;
    default:
      return null;
  }
};
